import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

const ToastContext = createContext(null);

let toastIdCounter = 0;

const ICONS = {
  success: 'fa-check-circle',
  error: 'fa-times-circle',
  warning: 'fa-exclamation-triangle',
  info: 'fa-info-circle',
};

const COLORS = {
  success: { bg: '#f0fdf4', border: '#4ade80', text: '#166534', icon: '#16a34a' },
  error: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', icon: '#dc2626' },
  warning: { bg: '#fffbeb', border: '#fbbf24', text: '#92400e', icon: '#d97706' },
  info: { bg: '#eff6ff', border: '#93c5fd', text: '#1e3a8a', icon: '#2563eb' },
};

/**
 * Wrap the app once with <ToastProvider> (done in App.js).
 * Anywhere else, call `const showToast = useToast()` then
 * `showToast('Votre message a été envoyé avec succès !', 'success')`.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback((message, type = 'success', duration = 4500) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    timers.current[id] = setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={showToast}>
      {children}

      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'fixed',
          top: '90px',
          right: '20px',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          maxWidth: 'min(380px, calc(100vw - 40px))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => {
          const c = COLORS[t.type] || COLORS.info;
          return (
            <div
              key={t.id}
              role="alert"
              style={{
                background: c.bg,
                border: `1.5px solid ${c.border}`,
                color: c.text,
                borderRadius: '12px',
                padding: '14px 16px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.92rem',
                lineHeight: 1.5,
                animation: 'aviatorToastIn 0.25s ease-out',
                pointerEvents: 'auto',
              }}
            >
              <i
                className={`fa ${ICONS[t.type] || ICONS.info}`}
                style={{ color: c.icon, fontSize: '1.15rem', marginTop: '2px', flexShrink: 0 }}
              />
              <div style={{ flex: 1, fontWeight: 500 }}>{t.message}</div>
              <button
                type="button"
                onClick={() => removeToast(t.id)}
                aria-label="Fermer la notification"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: c.text,
                  opacity: 0.55,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes aviatorToastIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 480px) {
          .aviator-toast-mobile-fix { right: 12px; left: 12px; }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

/**
 * Returns a `showToast(message, type, durationMs)` function.
 * type: 'success' | 'error' | 'warning' | 'info' (default 'success').
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be used inside a <ToastProvider>');
  }
  return ctx;
}
